import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// ─── Fallback ("Optiva") branding used when a store has no custom theme ───────
export const DEFAULT_BRANDING = {
  storeName:    'Optiva',
  tagline:      'Optical Excellence',
  logo:         null,
  primaryColor: '#10b981',
  accentColor:  '#6366f1',
  phone:        '',
  email:        '',
  city:         '',
  cityCode:     '',
  storeCode:    '',
  // Per-store invoice & receipt layout configs. Empty objects mean
  // "use the built-in defaults", so existing flows keep working until
  // an admin saves a custom template in StoreBrandingSettings.
  invoiceTemplate:    {},
  receiptTemplate:    {},
  orderNumberFormat:  '',
};

const BrandingContext = createContext({
  branding:        DEFAULT_BRANDING,
  updateBranding:  () => {},
  refreshBranding: () => {},
  clearBranding:   () => {},
});

const readUser = () => {
  try { return JSON.parse(localStorage.getItem('user') || 'null'); }
  catch { return null; }
};

const getStoreId = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object') return value._id || value.id || '';
  return '';
};

export const BrandingProvider = ({ children }) => {
  const [branding, setBranding] = useState(() => {
    try {
      const saved = localStorage.getItem('storeBranding');
      return saved ? { ...DEFAULT_BRANDING, ...JSON.parse(saved) } : DEFAULT_BRANDING;
    } catch {
      return DEFAULT_BRANDING;
    }
  });

  const updateBranding = useCallback((data) => {
    const next = { ...DEFAULT_BRANDING, ...data };
    setBranding(next);
    try { localStorage.setItem('storeBranding', JSON.stringify(next)); } catch (_) {}
  }, []);

  const clearBranding = useCallback(() => {
    setBranding(DEFAULT_BRANDING);
    try { localStorage.removeItem('storeBranding'); } catch (_) {}
  }, []);

  // Pull the freshest branding payload from the API. Used after admin saves
  // a template, on app boot, and on cross-tab login. Silent on failure —
  // the app keeps using whatever's in localStorage.
  const refreshBranding = useCallback(async (storeId) => {
    let id = storeId;
    if (!id) {
      const user = readUser();
      id = getStoreId(user?.store || user?.storeId);
    }
    id = getStoreId(id);
    if (!id) return null;
    try {
      const res = await fetch(`/api/stores/${id}/branding`, { cache: 'no-store' });
      if (!res.ok) return null;
      const { branding: fresh } = await res.json();
      if (fresh) updateBranding(fresh);
      return fresh;
    } catch (_) {
      return null;
    }
  }, [updateBranding]);

  // On boot, if a logged-in user already has a store, silently refresh
  // their cached branding so any template/label edits made by an admin in
  // a separate session show up immediately on next print.
  useEffect(() => {
    refreshBranding();
    // Also refresh when another tab updates auth (e.g. fresh login).
    const onStorage = (e) => {
      if (e.key === 'user' || e.key === 'token') refreshBranding();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [refreshBranding]);

  return (
    <BrandingContext.Provider
      value={{ branding, updateBranding, refreshBranding, clearBranding }}
    >
      {children}
    </BrandingContext.Provider>
  );
};

export const useBranding = () => useContext(BrandingContext);
export default BrandingContext;
